import React from "react";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DISPLAY_LOCATION_OPTIONS } from "../constants";
import type { DisplayLocation } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

export type DisplayLocationsFieldProps = {
  /** Injected by antd Form.Item. */
  value?: DisplayLocation[];
  onChange?: (next: DisplayLocation[]) => void;
  /**
   * View-only host (series child). Form.Item also injects `disabled` when the
   * parent Form is disabled — banner-only edit (`rulesLocked`) still needs
   * locations editable, so ignore that injection and dim only when this is set.
   */
  readOnly?: boolean;
};

/**
 * Multi-select toggle pills for a step's display locations (BONENG-921).
 * Selecting nothing means "not configured" — buildCampaignBody omits the key
 * and the backend places the bonus automatically. Shares the pill visuals with
 * ScheduleField, but with checkbox (multi-select) semantics.
 */
const DisplayLocationsField: React.FC<DisplayLocationsFieldProps> = ({
  value,
  onChange,
  readOnly = false,
}) => {
  const selected = value ?? [];
  const toggle = (location: DisplayLocation) => {
    if (readOnly) return;
    const next = selected.includes(location)
      ? selected.filter((v) => v !== location)
      : DISPLAY_LOCATION_OPTIONS().map((o) => o.value).filter(
          (v) => selected.includes(v) || v === location,
        );
    onChange?.(next);
  };
  return (
    <div data-testid="display-locations-field">
      <div
        role="group"
        aria-label={AppStrings.DISPLAY_LOCATIONS}
        className={styles.pillGroup}
      >
        {DISPLAY_LOCATION_OPTIONS().map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              disabled={readOnly}
              onClick={() => toggle(option.value)}
              className={[
                styles.pill,
                styles.displayLocationPill,
                isSelected ? styles.displayLocationPillSelected : "",
              ]
                .join(" ")
                .trim()}
              data-testid={`display-location-${option.value}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DisplayLocationsField;
